import React from 'react';
import { Link } from 'react-router-dom';
import { businessMetrics, formatters } from '../../config/businessMetrics';
import { appConfig } from '../../config/appConfig';
import BridgeAnimation from './BridgeAnimation';

const metrics = [
  {
    value: businessMetrics.buyers.displayText,
    label: 'Qualified Buyers',
    description: 'Active and vetted in our global network',
  },
  {
    value: `${formatters.currency(businessMetrics.deals.totalValue, true)}+`,
    label: 'Deal Volume',
    description: 'Total value we have advised',
  },
  {
    value: businessMetrics.timeline.fullProcess.displayText,
    label: 'Average Timeline',
    description: 'Preparation to close with disciplined process',
  },
  {
    value: businessMetrics.performance.higherValuations.displayText,
    label: 'Higher Valuations',
    description: 'Average uplift versus initial offers',
  },
];

const differentiators = [
  {
    title: 'Competition Creates Premium Outcomes',
    description:
      'We design tightly run buyer processes so every mandate starts with leverage and ends with multiple offers.',
    bullets: [
      'Average 3.5 qualified offers per engagement',
      '200k+ mapped buyers with clear investment theses',
      'Confidential outreach executed by senior advisors',
    ],
    icon: '📈',
  },
  {
    title: 'Radical Transparency with AMIVA+',
    description:
      'Our proprietary platform gives you real-time visibility into every outreach, NDA, and negotiation milestone.',
    bullets: [
      'Live dashboard with buyer engagement updates',
      'Centralized document room with institutional controls',
      'Weekly deal strategy reviews with actionable data',
    ],
    icon: '🧭',
  },
  {
    title: 'Boutique Team, Institutional Execution',
    description:
      'Seasoned operators lead every deal, combining Wall Street rigor with the responsiveness owners expect.',
    bullets: [
      'Senior deal team available when you need them',
      'Storytelling that resonates with strategic and financial buyers',
      'Success-based model aligned to your outcome',
    ],
    icon: '🤝',
  },
];

const timeline = [
  {
    year: '2014 – 2018',
    title: 'We Felt the Broken Process',
    description:
      'Our founders sold their own companies and experienced opaque brokerage models that left owners without leverage.',
  },
  {
    year: '2019',
    title: 'We Documented the Playbooks',
    description:
      'After advising on larger transactions, we codified how elite strategic and financial buyers evaluate deals.',
  },
  {
    year: '2020 – 2022',
    title: 'AMIVA+ Was Built and Battle-Tested',
    description:
      'We paired a transparent software layer with a hands-on advisory model and delivered faster, higher-value exits.',
  },
  {
    year: 'Today',
    title: 'Purpose-Built for Owners',
    description:
      'We run competitive processes for technology, healthcare, and service companies with one goal: maximize your outcome.',
  },
];

const values = [
  {
    title: 'Owners First, Always',
    description:
      'Every recommendation starts with your ideal outcome—if it does not serve the owner, we do not pursue it.',
    icon: '🎯',
  },
  {
    title: 'Show the Work',
    description:
      'Radical transparency means you see buyer engagement, negotiations, and next steps inside AMIVA+ in real time.',
    icon: '🔍',
  },
  {
    title: 'Competition Wins Deals',
    description:
      'We believe in creating informed demand, not waiting for it. More qualified buyers equals better terms for you.',
    icon: '⚡',
  },
];

const teamHighlights = [
  {
    name: 'Michael Chen',
    role: 'CEO & Co-Founder',
    summary:
      'Two-time founder who built and exited companies before launching AMIVA. Leads narrative development and buyer strategy.',
    focus: 'Deal Strategy & Storytelling',
  },
  {
    name: 'Sarah Williams',
    role: 'President & Co-Founder',
    summary:
      '20+ years helping owners exit. She engineered the AMIVA+ process to combine institutional buyer access with boutique attention.',
    focus: 'Valuation & Process Design',
  },
  {
    name: 'David Park',
    role: 'Head of M&A',
    summary:
      'Former private equity partner who now negotiates on behalf of owners. Brings the perspective of the buyers to every deal.',
    focus: 'Negotiations & Diligence',
  },
];

const testimonials = [
  {
    quote:
      "AMIVA's process was incredibly transparent. Watching buyer activity in real time gave our leadership confidence at every step.",
    name: 'Sarah Chen',
    title: 'Former CEO, TechFlow Solutions',
    result: '$12M exit • +45% over initial offer',
  },
  {
    quote:
      'They compressed an 18-month process into six while increasing competition. We finished with better terms than we imagined.',
    name: 'David Martinez',
    title: 'Founder, Precision Manufacturing',
    result: '$8M exit • +38% over initial offer',
  },
  {
    quote:
      'Multiple qualified buyers, structured communication, and a calm team pushing toward the best deal—we felt supported the entire way.',
    name: 'Dr. Jennifer Walsh',
    title: 'Owner, Healthcare Partners',
    result: '$15M exit • +40% over initial offer',
  },
];

export default function AboutSection() {
  return (
    <div className="bg-white">
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-sm font-semibold mb-6">
                Built for founders ready to exit
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Boutique M&A partners delivering
                <span className="text-gradient block">competitive outcomes with clarity</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl">
                AMIVA combines a senior advisory team with the AMIVA+ platform so you can see, shape, and celebrate every stage
                of your exit. We do the heavy lifting, you stay in control.
              </p>

              <ul className="space-y-3 mb-10">
                {[
                  'Structured buyer outreach built to maximize competition',
                  'AMIVA+ provides live visibility into every buyer action',
                  `${businessMetrics.team.experience.displayText} of collective team experience on your side`,
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-sm font-semibold">
                      ✓
                    </span>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/valuation" className="btn-primary inline-flex items-center justify-center text-base sm:text-lg">
                  Get Your Valuation
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center text-base sm:text-lg"
                >
                  Talk with Our Team
                </a>
              </div>
            </div>

            <div>
              <div className="card-gradient p-10 rounded-3xl shadow-xl border border-gray-200">
                <div className="text-sm font-semibold uppercase tracking-wider text-amiva-primary mb-6">
                  Outcomes that define us
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {metrics.map((metric) => (
                    <div key={metric.label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                      <div className="text-4xl font-bold text-gray-900 mb-2">{metric.value}</div>
                      <div className="text-sm font-semibold text-gray-700 mb-1">{metric.label}</div>
                      <p className="text-sm text-gray-500 leading-relaxed">{metric.description}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-sm text-gray-500">
                  Success fee only • Confidential by design • 24/7 access to your senior deal team
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-sm font-semibold mb-4">
              Our difference
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
              Modern sell-side advisory without the enterprise bureaucracy
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">
              Proprietary market intelligence, transparent technology, and a partner-level team drive outcomes that traditional brokers cannot match.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {differentiators.map((item) => (
              <div key={item.title} className="card-premium h-full p-10 flex flex-col">
                <div className="w-14 h-14 rounded-2xl bg-amiva-gradient-subtle flex items-center justify-center text-2xl mb-6">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 text-base leading-relaxed mb-6">{item.description}</p>
                <ul className="space-y-3 mt-auto">
                  {item.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3 text-sm text-gray-600">
                      <span className="mt-1 h-2 w-2 rounded-full bg-amiva-primary"></span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-start">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white text-amiva-primary text-sm font-semibold mb-4 shadow-sm">
                Our story
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Born from founder frustration, rebuilt for better exits
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                The AMIVA approach blends real-world operator empathy with institutional execution. We know the late-night questions, the diligence pressure, and the importance of every decision because we lived it.
              </p>
              <div className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100">
                <div className="text-sm font-semibold text-gray-700 mb-2">What this means for you</div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  <span className="font-semibold text-gray-900">No surprises.</span> Expect proactive guidance, structured buyer competition, and a timeline that keeps your deal moving.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-10">
              {timeline.map((item, index) => (
                <div key={item.title} className="relative pl-10 border-l border-gray-200">
                  <span className="absolute -left-3 top-1.5 h-6 w-6 rounded-full bg-white border-2 border-amiva-primary flex items-center justify-center text-xs font-semibold text-amiva-primary">
                    {index + 1}
                  </span>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                    <span className="text-sm font-semibold uppercase tracking-wider text-amiva-primary">{item.year}</span>
                    <span className="text-sm text-gray-500">Phase {index + 1}</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-base leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-20 rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-xl">
            <BridgeAnimation />
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-sm font-semibold mb-4">
              What guides us
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
              Principles that show up in every engagement
            </h2>
            <p className="text-lg text-gray-600">
              These values inform how we communicate, negotiate, and advocate for owners from kickoff to close.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value) => (
              <div key={value.title} className="card-premium h-full p-10 flex flex-col">
                <div className="w-16 h-16 rounded-2xl bg-amiva-gradient-subtle flex items-center justify-center text-3xl mb-6">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 text-base leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white text-amiva-primary text-sm font-semibold mb-4 shadow-sm">
              The team
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
              Senior operators that stay on your deal from day one
            </h2>
            <p className="text-lg text-gray-600">
              You work directly with partners who have closed dozens of transactions and know how to create leverage in the market.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {teamHighlights.map((member) => (
              <div key={member.name} className="card-premium p-10 h-full flex flex-col">
                <div className="text-sm font-semibold text-amiva-primary uppercase tracking-wider mb-2">
                  {member.role}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{member.name}</h3>
                <p className="text-gray-600 text-base leading-relaxed mb-6">{member.summary}</p>
                <div className="mt-auto pt-6 border-t border-gray-200 text-sm text-gray-500">
                  Focus: <span className="font-semibold text-gray-800">{member.focus}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-sm font-semibold mb-4">
              Client results
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5">
              Owners trust AMIVA when the outcome matters most
            </h2>
            <p className="text-lg text-gray-600">
              A transparent process creates confident decisions. Hear what recent clients experienced partnering with our team.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.name} className="card-gradient h-full p-10 flex flex-col">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">&ldquo;{testimonial.quote}&rdquo;</p>
                <div className="mt-auto pt-6 border-t border-gray-200">
                  <div className="text-sm font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500 mb-2">{testimonial.title}</div>
                  <div className="text-sm font-semibold text-amiva-primary">{testimonial.result}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-amiva-primary to-amiva-accent text-white p-10 md:p-16 shadow-2xl">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
              <div className="max-w-xl">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                  Ready to see what your business is worth in the right market?
                </h2>
                <p className="text-lg text-white/80 leading-relaxed">
                  Start with a confidential valuation or schedule time with our team. We will share benchmarks, prepare a tailored process, and move quickly when you are ready.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Link
                  to="/valuation"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white text-gray-900 font-semibold shadow-lg hover:shadow-xl transition"
                >
                  Start Your Valuation
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/60 text-white font-semibold hover:bg-white/10 transition"
                >
                  Schedule a Call
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
